// context.tsx — TramaProvider and useTramaClient hook; wraps TanStack QueryClientProvider
import React, { createContext, useContext, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TramaClient } from './client.js';

interface TramaContextValue {
  client: TramaClient;
}

const TramaContext = createContext<TramaContextValue | null>(null);

const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

export interface TramaProviderProps {
  apiKey: string;
  projectId: string;
  baseUrl?: string;
  queryClient?: QueryClient;
  children: React.ReactNode;
}

export function TramaProvider({
  apiKey,
  projectId,
  baseUrl,
  queryClient = defaultQueryClient,
  children,
}: TramaProviderProps): React.ReactElement {
  const client = useMemo(
    () => new TramaClient({ apiKey, projectId, ...(baseUrl != null ? { baseUrl } : {}) }),
    [apiKey, projectId, baseUrl],
  );

  return (
    <TramaContext.Provider value={{ client }}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </TramaContext.Provider>
  );
}

export function useTramaClient(): TramaClient {
  const ctx = useContext(TramaContext);
  if (!ctx) throw new Error('useTramaClient must be used inside <TramaProvider>');
  return ctx.client;
}
